import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import xhr from '@shared/config/api';
import type { Tag } from '@shared/types/common';

import type { CommonTag } from 'library/models/common';
import type {
	InviteResponse,
	ListResponse,
	Program,
	ProgramAssign,
	ProgramList,
	ProgramResponse,
	Specialization,
} from 'library/models/programs';
import type { ExerciseVideoResponse } from 'library/models/video';

const getSection = async (id: string | number): Promise<AxiosResponse<Specialization>> => {
	return xhr.get(`/api/v1/videos/section/${id}/`);
};

const getTags = async (): Promise<AxiosResponse<Tag[]>> => {
	return xhr.get(`/api/v1/program/tag/`);
};

const getVideoTags = async (): Promise<AxiosResponse<CommonTag[]>> => {
	return xhr.get(`/api/v1/common/tag/`);
};

const getPrograms = async (
	config?: AxiosRequestConfig
): Promise<AxiosResponse<ListResponse<ProgramList>>> => {
	return xhr.get(`/api/v1/program/all/`, config);
};

const getFavoritePrograms = (
	controller?: any,
	query?: string
): Promise<AxiosResponse<ProgramResponse>> => {
	return xhr.get(`/api/v1/program/favorite/?${query ?? ''}`, { signal: controller.signal });
};

const getMyPrograms = async (
	config?: AxiosRequestConfig
): Promise<AxiosResponse<ListResponse<ProgramList>>> => {
	return xhr.get(`/api/v1/program/all/my/`, config);
};

const getProgram = async (
	id: string,
	config?: AxiosRequestConfig
): Promise<AxiosResponse<Program>> => {
	return xhr.get(`/api/v1/program/all/${id}/`, config);
};

const getFullProgram = (id: string, config?: AxiosRequestConfig) => {
	return xhr.get(`/api/v1/program/program/full/${id}/`, config);
};

const getVideos = async (query?: string): Promise<AxiosResponse<ExerciseVideoResponse>> => {
	return xhr.get(`/api/v1/videos/exercise_videos/?${query ?? ''}`);
};

const createProgram = async (data: any, config: any): Promise<AxiosResponse<Program>> => {
	return xhr.post(`/api/v1/program/program/`, data, config);
};

const updateProgram = async (
	id: string | number,
	data: any,
	config?: any
): Promise<AxiosResponse<Program>> => {
	return xhr.patch(`/api/v1/program/all/${id}/`, data, config);
};

const removeProgram = async (id: string | number): Promise<AxiosResponse> => {
	return xhr.delete(`/api/v1/program/program/${id}/`);
};

const buyProgram = async (
	id: string | number
): Promise<AxiosResponse<{ redirect_url: string }>> => {
	return xhr.get(`/api/v1/program/program/buy/${id}/`);
};

const makeProgramFavorite = async (pk: string | number) => {
	return xhr.post(`/api/v1/program/favorite/`, { pk: pk });
};

const removeProgramFromFavorite = (pk: string | number) => {
	return xhr.delete(`/api/v1/program/favorite/${pk}/`);
};

const inviteToProgram = (
	pk: string | number,
	invite: { days: number; recommendation_comment?: string }
): Promise<AxiosResponse<{ hashcode: string }>> => {
	return xhr.post(`/api/v1/program/all/${pk}/invite/`, invite);
};

const subscribe = async (hash: string): Promise<AxiosResponse<InviteResponse>> => {
	return xhr.get(`/api/v1/program/all/${hash}/subscribe/`);
};

const recommend = async (data: ProgramAssign) => {
	return xhr.post(`/api/v1/program/individual/recommend/`, data);
};

const setStatus = async (id: number, data: any): Promise<AxiosResponse> => {
	return xhr.patch(`/api/v1/program/program/set_status/${id}/`, data);
};

const getSpecs = () => {
	return xhr.get(`/api/v1/program/specialization/`);
};

export const programsService = {
	getSection,
	getTags,
	getVideoTags,
	getPrograms,
	getFavoritePrograms,
	getMyPrograms,
	getProgram,
	getFullProgram,
	getVideos,
	createProgram,
	updateProgram,
	removeProgram,
	buyProgram,
	makeProgramFavorite,
	removeProgramFromFavorite,
	inviteToProgram,
	subscribe,
	recommend,
	setStatus,
	getSpecs,
};
